import { CRIMSON_NODE_TAG_PREFIX } from './crimsonNodeTags';

const WORDPROCESSINGML_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const DRAWINGML_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const DRAWINGML_PICTURE_NS = 'http://schemas.openxmlformats.org/drawingml/2006/picture';
const WORDPROCESSING_SHAPE_NS = 'http://schemas.microsoft.com/office/word/2010/wordprocessingShape';
const WORD_2012_NS = 'http://schemas.microsoft.com/office/word/2012/wordml';
const WORD_DOCUMENT_XML_PATH = 'word/document.xml';
const WORD_CONTENT_XML_PATH_PATTERN = /^word\/(?:document|header\d+|footer\d+|footnotes|endnotes)\.xml$/;

export async function normalizeDraftDocumentDocxForEditor(buffer: ArrayBuffer): Promise<ArrayBuffer> {
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    return buffer;
  }

  try {
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(buffer.slice(0));
    if (!zip.file(WORD_DOCUMENT_XML_PATH)) return buffer;

    let changed = false;

    for (const filePath of Object.keys(zip.files).filter((path) => WORD_CONTENT_XML_PATH_PATTERN.test(path))) {
      const part = zip.file(filePath);
      if (!part) continue;

      const xml = await part.async('string');
      const parsed = new DOMParser().parseFromString(xml, 'application/xml');
      if (parsed.getElementsByTagName('parsererror').length) {
        if (filePath === WORD_DOCUMENT_XML_PATH) return buffer;
        continue;
      }

      const normalizedLiteralTabs = normalizeLiteralTextTabsForSuperDoc(parsed);
      const normalizedFieldAnchors = normalizeFieldBearingCrimsonNodeAnchorsForSuperDoc(parsed);
      const normalizedSectionBreakAnchors = normalizeSectionBreakCrimsonNodeAnchorsForSuperDoc(parsed);
      const normalizedShapeImageFills = normalizeGroupedShapeImageFillsForSuperDoc(parsed);
      const addedEditableParagraph = filePath === WORD_DOCUMENT_XML_PATH
        ? ensureWordBodyHasEditableParagraph(parsed)
        : false;
      const partChanged = (
        normalizedLiteralTabs
        || normalizedFieldAnchors
        || normalizedSectionBreakAnchors
        || normalizedShapeImageFills
        || addedEditableParagraph
      );
      if (!partChanged) continue;

      zip.file(filePath, new XMLSerializer().serializeToString(parsed));
      changed = true;
    }

    if (!changed) return buffer;
    return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
  } catch {
    return buffer;
  }
}

/**
 * Removes the hidden Crimson anchor content controls so the editor sees plain
 * body content. Applied to the DOCX rather than the mounted document because
 * SuperDoc v2 exposes no editor view to rewrite after load.
 *
 * A block content control is a container, so the editor cannot merge a
 * paragraph out of one into another: Backspace at the start of a line selects
 * the preceding block instead of joining it. Word behaves the same way.
 *
 * Table wrappers have the same cost and a worse one: SuperDoc identifies a
 * table as `tbl:{cell paraId}` and projects its XML span from the inner
 * `<w:tbl>`. The wrapper's coordinates sit outside that span, so save throws
 * "tableNodeId coordinates do not cover the affected table blocks".
 *
 * Node identity survives the round trip on the `w14:paraId` anchors the
 * backend records: paragraphs and lists by their leading paragraph, tables by
 * the first paragraph of each cell. Canonical storage still re-wraps every
 * table on extract.
 */
export async function stripCrimsonNodeAnchorsFromDocx(buffer: ArrayBuffer): Promise<ArrayBuffer> {
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    return buffer;
  }

  try {
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(buffer.slice(0));
    if (!zip.file(WORD_DOCUMENT_XML_PATH)) return buffer;

    let changed = false;

    for (const filePath of Object.keys(zip.files).filter((path) => WORD_CONTENT_XML_PATH_PATTERN.test(path))) {
      const part = zip.file(filePath);
      if (!part) continue;

      const xml = await part.async('string');
      const parsed = new DOMParser().parseFromString(xml, 'application/xml');
      if (parsed.getElementsByTagName('parsererror').length) {
        if (filePath === WORD_DOCUMENT_XML_PATH) return buffer;
        continue;
      }

      if (!unwrapCrimsonNodeAnchors(parsed)) continue;

      zip.file(filePath, new XMLSerializer().serializeToString(parsed));
      changed = true;
    }

    if (!changed) return buffer;
    return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
  } catch {
    return buffer;
  }
}

export async function normalizeDraftDocumentDocxForPreview(buffer: ArrayBuffer): Promise<ArrayBuffer> {
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    return buffer;
  }

  try {
    const editorReadyBuffer = await normalizeDraftDocumentDocxForEditor(buffer);
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(editorReadyBuffer.slice(0));
    const documentXml = zip.file(WORD_DOCUMENT_XML_PATH);
    if (!documentXml) return editorReadyBuffer;

    const xml = await documentXml.async('string');
    if (!xml.includes('<w:tbl')) return editorReadyBuffer;

    const parsed = new DOMParser().parseFromString(xml, 'application/xml');
    if (parsed.getElementsByTagName('parsererror').length) return editorReadyBuffer;

    let changed = false;

    for (const table of Array.from(parsed.getElementsByTagNameNS(WORDPROCESSINGML_NS, 'tbl'))) {
      const { element: tblProperties, created: createdTblProperties } = ensureWordChildElement(
        parsed,
        table,
        'tblPr',
        true,
      );
      changed ||= createdTblProperties;

      const { element: tableWidth, created: createdTableWidth } = ensureWordChildElement(
        parsed,
        tblProperties,
        'tblW',
        true,
      );
      changed ||= createdTableWidth;

      if (getWordAttribute(tableWidth, 'type') !== 'pct') {
        setWordAttribute(tableWidth, 'type', 'pct');
        changed = true;
      }
      if (getWordAttribute(tableWidth, 'w') !== '5000') {
        setWordAttribute(tableWidth, 'w', '5000');
        changed = true;
      }
    }

    for (const tableRow of Array.from(parsed.getElementsByTagNameNS(WORDPROCESSINGML_NS, 'tr'))) {
      const { element: trProperties, created: createdTrProperties } = ensureWordChildElement(
        parsed,
        tableRow,
        'trPr',
        true,
      );
      changed ||= createdTrProperties;

      for (const tableHeader of Array.from(trProperties.children).filter((child) => child.localName === 'tblHeader')) {
        tableHeader.remove();
        changed = true;
      }

      const { element: cantSplit, created: createdCantSplit } = ensureWordChildElement(
        parsed,
        trProperties,
        'cantSplit',
      );
      changed ||= createdCantSplit;

      const value = getWordAttribute(cantSplit, 'val');
      if (value === '0' || value === 'false') {
        setWordAttribute(cantSplit, 'val', '1');
        changed = true;
      }
    }

    if (!changed) return editorReadyBuffer;

    zip.file(WORD_DOCUMENT_XML_PATH, new XMLSerializer().serializeToString(parsed));
    return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
  } catch {
    return buffer;
  }
}

function normalizeLiteralTextTabsForSuperDoc(xmlDocument: XMLDocument): boolean {
  let changed = false;

  // Word accepts literal tab characters inside w:t, but SuperDoc only lays
  // them out reliably when they use WordprocessingML's explicit w:tab node.
  for (const textElement of Array.from(xmlDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, 't'))) {
    const value = textElement.textContent ?? '';
    if (!value.includes('\t') || !textElement.parentNode) continue;

    const fragments = value.split('\t');
    fragments.forEach((fragment, index) => {
      if (fragment) {
        const replacementText = textElement.cloneNode(false) as Element;
        replacementText.textContent = fragment;
        textElement.parentNode?.insertBefore(replacementText, textElement);
      }
      if (index < fragments.length - 1) {
        const tab = xmlDocument.createElementNS(WORDPROCESSINGML_NS, 'w:tab');
        textElement.parentNode?.insertBefore(tab, textElement);
      }
    });
    textElement.remove();
    changed = true;
  }

  return changed;
}

function normalizeFieldBearingCrimsonNodeAnchorsForSuperDoc(xmlDocument: XMLDocument): boolean {
  let changed = false;

  for (const story of wordStoryContainers(xmlDocument)) {
    let fieldDepth = 0;

    for (const child of Array.from(story.children)) {
      const contentControl = isWordElement(child, 'sdt') && isCrimsonNodeContentControl(child) ? child : null;
      const content = contentControl ? firstChildElementByLocalName(contentControl, 'sdtContent') : null;
      const fieldScope = content ?? child;
      const generatedFieldBlock = fieldDepth > 0 || (content ? isNativeFieldOwnedContent(content) : false);
      const nextFieldDepth = complexFieldDepthAfter(fieldScope, fieldDepth);

      if (contentControl && content && containsNativeWordField(content)) {
        if (generatedFieldBlock) {
          unwrapElement(contentControl, content);
          changed = true;
        } else if (removeHiddenContentControlAppearance(contentControl)) {
          changed = true;
        }
      }

      fieldDepth = nextFieldDepth;
    }
  }

  return changed;
}

function normalizeSectionBreakCrimsonNodeAnchorsForSuperDoc(xmlDocument: XMLDocument): boolean {
  let changed = false;

  for (const body of Array.from(xmlDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, 'body'))) {
    for (const child of Array.from(body.children)) {
      if (!isWordElement(child, 'sdt') || !isCrimsonNodeContentControl(child)) continue;

      const content = firstChildElementByLocalName(child, 'sdtContent');
      if (!content || !containsOnlyWordSectionBreakContent(content)) continue;

      unwrapElement(child, content);
      changed = true;
    }
  }

  return changed;
}

function unwrapCrimsonNodeAnchors(xmlDocument: XMLDocument): boolean {
  let changed = false;

  for (const story of wordStoryContainers(xmlDocument)) {
    for (const child of Array.from(story.children)) {
      if (!isWordElement(child, 'sdt') || !isCrimsonNodeContentControl(child)) continue;

      const content = firstChildElementByLocalName(child, 'sdtContent');
      if (!content) continue;

      unwrapElement(child, content);
      changed = true;
    }
  }

  return changed;
}

function wordStoryContainers(xmlDocument: XMLDocument): Element[] {
  return ['body', 'hdr', 'ftr', 'footnote', 'endnote'].flatMap(localName => (
    Array.from(xmlDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, localName))
  ));
}

function removeHiddenContentControlAppearance(contentControl: Element): boolean {
  const properties = firstChildElementByLocalName(contentControl, 'sdtPr');
  if (!properties) return false;

  let changed = false;
  for (const appearance of Array.from(properties.children).filter(isHiddenContentControlAppearance)) {
    appearance.remove();
    changed = true;
  }

  return changed;
}

function unwrapElement(wrapper: Element, content: Element): void {
  const parent = wrapper.parentNode;
  if (!parent) return;

  while (content.firstChild) {
    parent.insertBefore(content.firstChild, wrapper);
  }
  parent.removeChild(wrapper);
}

function isCrimsonNodeContentControl(contentControl: Element): boolean {
  const properties = firstChildElementByLocalName(contentControl, 'sdtPr');
  const tag = properties ? firstChildElementByLocalName(properties, 'tag') : null;
  const value = tag ? getWordAttribute(tag, 'val') : null;
  return value?.startsWith(CRIMSON_NODE_TAG_PREFIX) ?? false;
}

function containsNativeWordField(element: Element): boolean {
  return Boolean(
    element.getElementsByTagNameNS(WORDPROCESSINGML_NS, 'fldChar').length
    || element.getElementsByTagNameNS(WORDPROCESSINGML_NS, 'instrText').length
    || element.getElementsByTagNameNS(WORDPROCESSINGML_NS, 'fldSimple').length,
  );
}

function containsOnlyWordSectionBreakContent(element: Element): boolean {
  let hasSectionProperties = false;

  for (const child of Array.from(element.children)) {
    if (isWordElement(child, 'sectPr')) {
      hasSectionProperties = true;
      continue;
    }
    if (!isWordElement(child, 'p')) return false;
    if (!containsWordSectionProperties(child)) return false;
    if (paragraphHasContentOutsideProperties(child)) return false;
    hasSectionProperties = true;
  }

  return hasSectionProperties;
}

function containsWordSectionProperties(element: Element): boolean {
  return Boolean(element.getElementsByTagNameNS(WORDPROCESSINGML_NS, 'sectPr').length);
}

function paragraphHasContentOutsideProperties(paragraph: Element): boolean {
  return Array.from(paragraph.children).some(child => !isWordElement(child, 'pPr'));
}

function isNativeFieldOwnedContent(element: Element): boolean {
  let hasField = false;
  let fieldDepth = 0;
  const authoredParts: string[] = [];

  const visitChildren = (parent: Node, insideSimpleField: boolean): void => {
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType !== Node.ELEMENT_NODE) continue;

      const childElement = child as Element;
      if (isWordElement(childElement, 'fldSimple')) {
        hasField = true;
        visitChildren(childElement, true);
        continue;
      }
      if (isWordElement(childElement, 'fldChar')) {
        hasField = true;
        const fieldType = getWordAttribute(childElement, 'fldCharType');
        if (fieldType === 'begin') {
          fieldDepth += 1;
        } else if (fieldType === 'end') {
          fieldDepth = Math.max(0, fieldDepth - 1);
        }
        continue;
      }
      if (isWordElement(childElement, 'instrText')) {
        hasField = true;
      }
      if (!insideSimpleField && fieldDepth === 0 && isVisibleWordTextElement(childElement)) {
        authoredParts.push(visibleWordTextElementValue(childElement));
      }
      visitChildren(childElement, insideSimpleField);
    }
  };

  visitChildren(element, false);
  return hasField && cleanText(authoredParts.join('')) === '';
}

function complexFieldDepthAfter(element: Element, startDepth: number): number {
  let fieldDepth = startDepth;

  const visitChildren = (parent: Node): void => {
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType !== Node.ELEMENT_NODE) continue;

      const childElement = child as Element;
      if (isWordElement(childElement, 'fldSimple')) continue;
      if (isWordElement(childElement, 'fldChar')) {
        const fieldType = getWordAttribute(childElement, 'fldCharType');
        if (fieldType === 'begin') {
          fieldDepth += 1;
        } else if (fieldType === 'end') {
          fieldDepth = Math.max(0, fieldDepth - 1);
        }
        continue;
      }
      visitChildren(childElement);
    }
  };

  visitChildren(element);
  return fieldDepth;
}

function isWordElement(element: Element, localName: string): boolean {
  return element.namespaceURI === WORDPROCESSINGML_NS && element.localName === localName;
}

function isVisibleWordTextElement(element: Element): boolean {
  return ['t', 'tab', 'br', 'cr'].includes(element.localName) && element.namespaceURI === WORDPROCESSINGML_NS;
}

function visibleWordTextElementValue(element: Element): string {
  if (element.localName === 'tab') return '\t';
  if (element.localName === 'br' || element.localName === 'cr') return '\n';
  return element.textContent ?? '';
}

function cleanText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function isHiddenContentControlAppearance(element: Element): boolean {
  if (element.localName !== 'appearance') return false;
  return getWord2012Attribute(element, 'val') === 'hidden';
}

function getWord2012Attribute(element: Element, localName: string): string | null {
  return element.getAttributeNS(WORD_2012_NS, localName) ?? element.getAttribute(`w15:${localName}`);
}

/**
 * Converts image-filled shapes inside a DrawingML group into grouped pictures.
 *
 * SuperDoc renders a picture (`pic:pic`) but not a shape whose fill is an image
 * (`a:blipFill` on `wps:spPr`) -- the latter falls back to the striped drawing
 * placeholder. Upstream support for picture-filled vector shapes is still an
 * unmerged pull request (superdoc-dev/superdoc#3779), so keep converting until
 * that ships. Shapes carrying a text box are left alone because a picture
 * cannot hold their content.
 */
function normalizeGroupedShapeImageFillsForSuperDoc(xmlDocument: XMLDocument): boolean {
  let changed = false;

  for (const shape of Array.from(xmlDocument.getElementsByTagNameNS(WORDPROCESSING_SHAPE_NS, 'wsp'))) {
    const parentLocalName = shape.parentElement?.localName;
    if (parentLocalName !== 'wgp' && parentLocalName !== 'grpSp') continue;
    if (firstChildElementByLocalName(shape, 'txbx')) continue;

    const shapeProperties = firstChildElementByLocalName(shape, 'spPr');
    const imageFill = shapeProperties ? firstChildElementByLocalName(shapeProperties, 'blipFill') : null;
    if (!shapeProperties || !imageFill || !firstChildElementByLocalName(imageFill, 'blip')) continue;

    shape.replaceWith(createPictureFromShapeImageFill(xmlDocument, shape, shapeProperties, imageFill));
    changed = true;
  }

  return changed;
}

function createPictureFromShapeImageFill(
  xmlDocument: XMLDocument,
  shape: Element,
  shapeProperties: Element,
  imageFill: Element,
): Element {
  const picture = xmlDocument.createElementNS(DRAWINGML_PICTURE_NS, 'pic:pic');
  picture.appendChild(createPictureNonVisualProperties(xmlDocument, shape));

  const pictureImageFill = xmlDocument.createElementNS(DRAWINGML_PICTURE_NS, 'pic:blipFill');
  copyAttributes(imageFill, pictureImageFill);
  for (const child of Array.from(imageFill.childNodes)) {
    pictureImageFill.appendChild(child.cloneNode(true));
  }
  picture.appendChild(pictureImageFill);

  const pictureShapeProperties = xmlDocument.createElementNS(DRAWINGML_PICTURE_NS, 'pic:spPr');
  for (const child of Array.from(shapeProperties.childNodes)) {
    if (child.nodeType !== Node.ELEMENT_NODE || (child as Element).localName !== 'blipFill') {
      pictureShapeProperties.appendChild(child.cloneNode(true));
    }
  }
  picture.appendChild(pictureShapeProperties);

  return picture;
}

function createPictureNonVisualProperties(xmlDocument: XMLDocument, shape: Element): Element {
  const sourceNonVisualProperties = firstChildElementByLocalName(shape, 'cNvPr');
  const pictureNonVisualProperties = xmlDocument.createElementNS(DRAWINGML_PICTURE_NS, 'pic:nvPicPr');
  const nonVisualDrawingProperties = xmlDocument.createElementNS(DRAWINGML_PICTURE_NS, 'pic:cNvPr');

  if (sourceNonVisualProperties) {
    copyAttributes(sourceNonVisualProperties, nonVisualDrawingProperties);
    for (const child of Array.from(sourceNonVisualProperties.childNodes)) {
      nonVisualDrawingProperties.appendChild(child.cloneNode(true));
    }
  }

  if (!nonVisualDrawingProperties.getAttribute('id')) {
    nonVisualDrawingProperties.setAttribute('id', '0');
  }
  if (!nonVisualDrawingProperties.getAttribute('name')) {
    nonVisualDrawingProperties.setAttribute('name', 'Picture');
  }

  const nonVisualPictureDrawingProperties = xmlDocument.createElementNS(DRAWINGML_PICTURE_NS, 'pic:cNvPicPr');
  const pictureLocks = xmlDocument.createElementNS(DRAWINGML_NS, 'a:picLocks');
  pictureLocks.setAttribute('noChangeAspect', '1');
  nonVisualPictureDrawingProperties.appendChild(pictureLocks);

  pictureNonVisualProperties.appendChild(nonVisualDrawingProperties);
  pictureNonVisualProperties.appendChild(nonVisualPictureDrawingProperties);
  return pictureNonVisualProperties;
}

function copyAttributes(source: Element, target: Element): void {
  for (const attribute of Array.from(source.attributes)) {
    target.setAttributeNS(attribute.namespaceURI, attribute.name, attribute.value);
  }
}

function ensureWordBodyHasEditableParagraph(xmlDocument: XMLDocument): boolean {
  const body = xmlDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, 'body')[0];
  if (!body) return false;

  const hasEditableBodyChild = Array.from(body.children).some((child) => child.localName !== 'sectPr');
  if (hasEditableBodyChild) return false;

  const paragraph = xmlDocument.createElementNS(WORDPROCESSINGML_NS, 'w:p');
  const sectionProperties = firstChildElementByLocalName(body, 'sectPr');
  body.insertBefore(paragraph, sectionProperties ?? null);
  return true;
}

function firstChildElementByLocalName(parent: Element, localName: string): Element | null {
  return Array.from(parent.children).find((child) => child.localName === localName) ?? null;
}

function ensureWordChildElement(
  xmlDocument: XMLDocument,
  parent: Element,
  localName: string,
  insertAtStart = false,
): { element: Element; created: boolean } {
  const existing = firstChildElementByLocalName(parent, localName);
  if (existing) return { element: existing, created: false };

  const element = xmlDocument.createElementNS(WORDPROCESSINGML_NS, `w:${localName}`);
  if (insertAtStart) {
    parent.insertBefore(element, parent.firstChild);
  } else {
    parent.appendChild(element);
  }
  return { element, created: true };
}

function getWordAttribute(element: Element, localName: string): string | null {
  return element.getAttributeNS(WORDPROCESSINGML_NS, localName) ?? element.getAttribute(`w:${localName}`);
}

function setWordAttribute(element: Element, localName: string, value: string): void {
  element.setAttributeNS(WORDPROCESSINGML_NS, `w:${localName}`, value);
}
